import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import { TProductVendor } from "redux/reducers/models/product";
import axios, { CancelToken } from "axios";
import { GetVendor, PostVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize, errorMessage, valueToString } from "helpers/Helpers";
import ReactDOM from "react-dom";
import validator from "validator";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { useHistory } from "react-router";
import { PostCreditMemo } from "api/rpc/2024-04/facilityAdmin/order/creditMemo";
import { IPurchaseOrder } from "redux/reducers/models/purchaseOrder";
import { GetPurchaseOrder } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import moment from "moment";
import { Badge } from "components/badge/Badge";

interface IState {
    facility_name: string;
    facility_address_line: string;
    facility_city: string;
    facility_province: string;
    facility_postal: string;
    facility_country: string;

    // Vendor
    vendor_name: string;
    vendor_address_line: string;
    vendor_city: string;
    vendor_province: string;
    vendor_postal: string;
    vendor_country: string;
    vendor_email: string;
    vendor_phone: string;
    vendor_contact_name: string;
    credit_memo_number: string;
}

export default function CreditMemoNew() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { facilityStore } = useAppSelector(store => store);
    //Purchase orders
    const [POSearch, setPOSearch] = useState<string>("");
    const [purchaseOrders, setPurchaseOrders] = useState<Array<IPurchaseOrder>>([]);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<IPurchaseOrder>(null);

    //Vendors
    const [vendorSearch, setVendorSearch] = useState<string>("");
    const [vendors, setVendors] = useState<Array<TProductVendor>>([]);
    const [selectedVendor, setSelectedVendor] = useState<TProductVendor>(null);

    const { state: newVendorModal, updateModal: updateNewVendorModal, closeModal: closeNewVendorModal } = useModal<{ title: string; address_line: string; city: string; province: string; country: string; postal_code: string; email: string; phone: string; contact_name: "" }>({
        title: "",
        address_line: "",
        city: "",
        province: "",
        country: "",
        postal_code: "",
        email: "",
        phone: "",
        contact_name: "",
    });

    const { Option } = Select;
    const [state, setState] = useState<IState>({
        facility_name: "",
        facility_address_line: "",
        facility_city: "",
        facility_province: "",
        facility_postal: "",
        facility_country: "",

        // Vendor
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_postal: "",
        vendor_country: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
        credit_memo_number: "",
    });

    useEffect(() => {
        if (facilityStore?.facility) {
            setState(prev => ({
                ...prev,
                facility_name: facilityStore?.facility?.long_name,
                facility_address_line: facilityStore?.facility?.address_line_1,
                facility_city: facilityStore?.facility?.city,
                facility_province: facilityStore?.facility?.province_name,
                facility_postal: facilityStore?.facility?.postal,
                facility_country: facilityStore?.facility?.country_name,
            }));
        }
    }, [facilityStore?.facility]);

    useEffect(() => {
        const source = axios.CancelToken.source();

        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void searchVendors(source.token, vendorSearch);
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            setVendors([]);
            source.cancel();
        };
    }, [vendorSearch]);


    useEffect(() => {
        const source = axios.CancelToken.source();

        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void loadPurchaseOrders(source.token);
        }, 300);

        return () => {
            source.cancel();
            clearTimeout(timeoutId);
        };
    }, [POSearch]);

    async function loadPurchaseOrders(token?: CancelToken) {
        //Show loader
        if (purchaseOrders) {
            setPurchaseOrders(undefined);
        }

        const res = await GetPurchaseOrder({ search: POSearch, statuses: ["ordered", "partially_received", "received", "closed"], year: new Date()?.getFullYear() }, token ? false : true, token);

        if (res.status !== StatusCode.OK) {
            setPurchaseOrders([]);
            if (token && token.reason) {
                return;
            }
            dispatch(showError(t("Error getting purchase orders")));
        }
        setPurchaseOrders(res?.data);
    }

    async function searchVendors(cancelToken: CancelToken, searchQuery: string) {
        if (vendors) {
            setVendors(undefined);
        }
        const vendorRes = await GetVendor({ search: searchQuery }, false, cancelToken);
        if (vendorRes?.status !== StatusCode.OK) {
            setVendors([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(vendorRes, t("Error getting vendors"))));
            return;
        }
        setVendors(vendorRes?.data);
    }

    function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        setState(prev => ({ ...prev, [id]: value }));
    }


    function vendorModalInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateNewVendorModal({ [id]: value });
    }

    async function saveNewVendor() {
        const params = {
            ...newVendorModal,
        };

        if (newVendorModal?.email && !validator?.isEmail(newVendorModal?.email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }

        const res = await PostVendor(params, true);
        if (res.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating vendor"))));
            return;
        }

        dispatch(showSuccess(t("Successfully created vendor")));
        const vendor = res?.data;
        ReactDOM.unstable_batchedUpdates(() => {
            setVendorSearch(vendor?.title);
            setState(prev => ({
                ...prev,
                vendor_name: vendor?.title ?? "",
                vendor_address_line: vendor?.address_line ?? "",
                vendor_city: vendor?.city ?? "",
                vendor_province: vendor?.province ?? "",
                vendor_postal: vendor?.postal_code ?? "",
                vendor_country: vendor?.country ?? "",
                vendor_email: vendor?.email ?? "",
                vendor_phone: vendor?.phone ?? "",
                vendor_contact_name: vendor?.contact_name ?? "",
            }));
        });
        void closeNewVendorModal();
    }

    async function createCreditMemo() {

        if (state?.vendor_email && !validator.isEmail(state?.vendor_email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }

        const res = await PostCreditMemo({
            vendor_id: selectedVendor?.id ?? null,
            purchase_order_id: selectedPurchaseOrder?.id ?? null,

            status: "draft",
            credit_memo_number: state.credit_memo_number,

            facility_name: state.facility_name,
            facility_address_line: state.facility_address_line,
            facility_city: state.facility_city,
            facility_province: state.facility_province,
            facility_postal: state.facility_postal,
            facility_country: state.facility_country,

            // Vendor
            vendor_name: state.vendor_name,
            vendor_address_line: state.vendor_address_line,
            vendor_city: state.vendor_city,
            vendor_province: state.vendor_province,
            vendor_postal: state.vendor_postal,
            vendor_country: state.vendor_country,
            vendor_contact_name: state.vendor_contact_name || null,
            vendor_email: state.vendor_email || null,
            vendor_phone: state.vendor_phone || null,

            notes: null,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating credit memo"))));
            return;
        }
        dispatch(showSuccess(t("Successfully created credit memo draft")));
        history.push(`/admin/settings/credit-memos/${res?.data?.id}`);
    }

    return (
        <>

            <Page
                title={t("New credit memo")}
                narrow
                breadcrumbs={[{
                    prefix: true,
                    label: t("Back to credit Memos"),
                    url: "/admin/settings/credit-memos",
                }]}
                primaryAction={{ content: t("Create"), action: () => createCreditMemo() }}
            >
                <Card title={t("Credit Memo Details")}>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Credit Memo Number")}
                                    value={state?.credit_memo_number}
                                    onChange={onInputChange}
                                    id="credit_memo_number"
                                    placeholder={t("Credit Memo Number")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Select
                                    label={t("Purchase Order")}
                                    onChange={(_, purchaseOrder: IPurchaseOrder) => {
                                        const vendor = vendors?.find(vendor => vendor?.id === purchaseOrder?.vendor_id);
                                        ReactDOM.unstable_batchedUpdates(() => {
                                            setSelectedPurchaseOrder(purchaseOrder);
                                            setPOSearch(`${purchaseOrder?.po_number} - ${purchaseOrder?.vendor_name} ${moment.utc(purchaseOrder?.ordered_at ?? purchaseOrder?.created_at, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LL")}`);
                                            if (!selectedVendor && vendor) {
                                                setSelectedVendor(vendor);
                                                setVendorSearch(vendor?.title);
                                                setState(prev => ({
                                                    ...prev,
                                                    vendor_name: vendor?.title ?? "",
                                                    vendor_address_line: vendor?.address_line ?? "",
                                                    vendor_city: vendor?.city ?? "",
                                                    vendor_province: vendor?.province ?? "",
                                                    vendor_postal: vendor?.postal_code ?? "",
                                                    vendor_country: vendor?.country ?? "",
                                                    vendor_email: vendor?.email ?? "",
                                                    vendor_phone: vendor?.phone ?? "",
                                                    vendor_contact_name: vendor?.contact_name ?? "",
                                                }));
                                            }
                                        });
                                    }
                                    }
                                    showSearch
                                    searchValue={POSearch}
                                    searching={purchaseOrders === undefined}
                                    onSearch={(query: string) => setPOSearch(query)}
                                    showDropDownOnFocus
                                    allowClear
                                    usePortal
                                    placeholder={t("Search purchase orders...")}
                                >
                                    {purchaseOrders?.map((purchaseOrder, index) => {
                                        return (
                                            <Option key={index} value={purchaseOrder?.id} extraValues={purchaseOrder}>
                                             {purchaseOrder?.po_number} - {purchaseOrder?.vendor_name} <span className="font-medium text-gray-500 text-sm">{moment.utc(purchaseOrder?.ordered_at ?? purchaseOrder?.created_at, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LL")} <Badge size="small" type={purchaseOrder?.status === "closed" ? "success" : purchaseOrder?.status === "cancelled" ? "error" : "gray"}>{valueToString(purchaseOrder?.status)}</Badge></span>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Select
                                    label={t("Vendor")}
                                    placeholder={t("Vendor")}
                                    showSearch
                                    onSearch={(query: string) => {
                                        setVendorSearch(query);
                                        if (!query) {
                                            setSelectedVendor(null);
                                        }
                                    }}
                                    onChange={(_, vendor: TProductVendor) => {
                                        if (vendor) {
                                            ReactDOM.unstable_batchedUpdates(() => {
                                                setSelectedVendor(vendor);
                                                setVendorSearch(vendor?.title);
                                                setState(prev => ({
                                                    ...prev,
                                                    vendor_name: vendor?.title ?? "",
                                                    vendor_address_line: vendor?.address_line ?? "",
                                                    vendor_city: vendor?.city ?? "",
                                                    vendor_province: vendor?.province ?? "",
                                                    vendor_postal: vendor?.postal_code ?? "",
                                                    vendor_country: vendor?.country ?? "",
                                                    vendor_email: vendor?.email ?? "",
                                                    vendor_phone: vendor?.phone ?? "",
                                                    vendor_contact_name: vendor?.contact_name ?? "",
                                                }));
                                            });
                                        }
                                    }}
                                    allowClear
                                    searchValue={vendorSearch}
                                    showDropDownOnFocus={true}
                                    searching={vendors === undefined}
                                    defaultValue={selectedVendor?.id}
                                    usePortal
                                >
                                    <div
                                        className="ui-select-dropdown-list-item"
                                        onClick={() => updateNewVendorModal({ isOpen: true, title: capitalize(vendorSearch ?? "") })}
                                    >
                                        <p className="text-md">{t("New")}</p>
                                    </div>
                                    {vendors?.map((vendor, index) => {
                                        return (
                                            <Option key={index} value={vendor.id} extraValues={vendor}>
                                                {vendor?.title}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Vendor Name")}
                                    value={state?.vendor_name}
                                    onChange={onInputChange}
                                    id="vendor_name"
                                    placeholder={t("Vendor Name")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Address Line")}
                                    value={state?.vendor_address_line}
                                    onChange={onInputChange}
                                    id="vendor_address_line"
                                    placeholder={t("Address Line")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("City")}
                                    value={state?.vendor_city}
                                    onChange={onInputChange}
                                    id="vendor_city"
                                    placeholder={t("City")}
                                />
                                <Input
                                    label={t("Province")}
                                    value={state?.vendor_province}
                                    onChange={onInputChange}
                                    id="vendor_province"
                                    placeholder={t("Province")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Country")}
                                    value={state?.vendor_country}
                                    onChange={onInputChange}
                                    id="vendor_country"
                                    placeholder={t("Country")}
                                />
                                <Input
                                    label={t("Postal Code")}
                                    value={state?.vendor_postal}
                                    onChange={onInputChange}
                                    id="vendor_postal"
                                    placeholder={t("Postal Code")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Email")}
                                    value={state?.vendor_email}
                                    onChange={onInputChange}
                                    id="vendor_email"
                                    placeholder={t("Email")}
                                    type="email"
                                    error={state?.vendor_email && !validator.isEmail(state?.vendor_email)}
                                />
                                <Input
                                    label={t("Phone")}
                                    value={state?.vendor_phone}
                                    onChange={onInputChange}
                                    id="vendor_phone"
                                    placeholder={t("Phone")}
                                    type="number"
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Contact Name")}
                                    value={state?.vendor_contact_name}
                                    onChange={onInputChange}
                                    id="vendor_contact_name"
                                    placeholder={t("Contact Name")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                </Card>
                <Card title={t("Facility")}>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Facility Name")}
                                    value={state?.facility_name}
                                    onChange={onInputChange}
                                    id="facility_name"
                                    placeholder={t("Facility Name")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Address Line")}
                                    value={state?.facility_address_line}
                                    onChange={onInputChange}
                                    id="facility_address_line"
                                    placeholder={t("Address Line")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("City")}
                                    value={state?.facility_city}
                                    onChange={onInputChange}
                                    id="facility_city"
                                    placeholder={t("City")}
                                />
                                <Input
                                    label={t("Province")}
                                    value={state?.facility_province}
                                    onChange={onInputChange}
                                    id="facility_province"
                                    placeholder={t("Province")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Country")}
                                    value={state?.facility_country}
                                    onChange={onInputChange}
                                    id="facility_country"
                                    placeholder={t("Country")}
                                />
                                <Input
                                    label={t("Postal Code")}
                                    value={state?.facility_postal}
                                    onChange={onInputChange}
                                    id="facility_postal"
                                    placeholder={t("Postal Code")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                </Card>
            </Page>
            <Portal isMounted={newVendorModal?.isOpen}>
                <Sheet
                    title={t("New vendor")}
                    open={newVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNewVendorModal}
                    onOk={saveNewVendor}
                    okText={t("Create")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.title}
                                label={t("Name")}
                                id="title"
                                onChange={vendorModalInputChange}
                                placeholder={t("Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.address_line}
                                label={t("Address")}
                                id="address_line"
                                onChange={vendorModalInputChange}
                                placeholder={t("Address")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.city}
                                label={t("City")}
                                id="city"
                                onChange={vendorModalInputChange}
                                placeholder={t("City")}
                            />
                            <Input
                                value={newVendorModal.province}
                                label={t("Province")}
                                id="province"
                                onChange={vendorModalInputChange}
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.country}
                                label={t("Country")}
                                id="country"
                                onChange={vendorModalInputChange}
                                placeholder={t("Country")}
                            />
                            <Input
                                value={newVendorModal.postal_code}
                                label={t("Postal Code")}
                                id="postal_code"
                                onChange={vendorModalInputChange}
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.email}
                                label={t("Email")}
                                id="email"
                                type="email"
                                onChange={vendorModalInputChange}
                                placeholder={t("Email")}
                                error={newVendorModal?.email && !validator.isEmail(newVendorModal?.email)}
                            />
                            <Input
                                value={newVendorModal.phone}
                                label={t("Phone")}
                                id="phone"
                                type="number"
                                onChange={vendorModalInputChange}
                                placeholder={t("Phone")}
                                error={newVendorModal?.phone && !validator?.isNumeric(newVendorModal?.phone)}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.contact_name}
                                label={t("Contact Name")}
                                id="contact_name"
                                onChange={vendorModalInputChange}
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>
        </>
    );
}